/* eslint-disable no-unused-vars */
import React, { useRef, useState, useEffect } from 'react';
import { Field, ErrorMessage } from 'formik';
import {
   Grid,
   Box,
   Typography,
   MenuItem,
   Select,
   FormControl,
   InputLabel,
   FormHelperText,
   Chip,
   TextField,
} from '@mui/material';
import CustomDatePicker from '../../Components/MUI/CustomDatePicker';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '../../Utils/RichEditor';
import { useSelector } from 'react-redux';
import {
   setContractCurrency,
   setContractPrefilledData,
   setSelectedContractTypeId,
   setContractTypePreFillData,
} from '../../Redux/Reducers/Contracts/ContractSlice';
import { useDispatch } from 'react-redux';
import InfiniteAutocomplete from '../../Components/InfiniteAutocomplete';
import { useGetopportunitiesQuery } from '../../Redux/Service/OpportunityApi';
import axios from 'axios';
import API from '../../Web/Instance';
import { API_URL } from '../../Config/index';
import { getAuthToken } from '../../Redux/Service/FetchBaseQuery';
import { useGetProvidersQuery } from '../../Redux/Service/ProviderApi';
import {
   useGetActiveContractTypesQuery,
   useGetContractTypeByIdQuery,
} from '../../Redux/Service/ContractTypeApi';
import { extractPreFillValues } from '../../Utils/contractFieldHelpers';

// NEW: shared max-length limit for the CKEditor fields that show a character count
const FIELD_MAX_LENGTHS = {
   confidential_information: 5000,
   purpose_of_the_agreement: 1000,
   scope_of_work: 1000,
   amendments: 5000,
};

function StepOne({
   isSubmitting,
   values,
   setFieldValue,
   touched,
   errors,
   initialValues,
   handleBlur,
   activeStep,
}) {
   // Redux State for Contract Prefilled Data
   const contractPrefilledData = useSelector(
      (state) => state.contractState?.prefilledData?.response,
   );
   const contractdata = useSelector((state) => state.contractState);
   const dispatch = useDispatch();

   // States for InfiniteAutocomplete
   const [search, setSearch] = useState('');
   const [page, setPage] = useState(1);
   const [visibleOptions, setVisibleOptions] = useState([]);
   const [selectedOption, setSelectedOption] = useState(null);
   const debounceTimeout = useRef(null);

   // State for milestones
   const [milestones, setMilestones] = useState([]);
   const [isLoadingMilestones, setIsLoadingMilestones] = useState(false);

   // State for estimates
   const [estimates, setEstimates] = useState([]);
   const [isLoadingEstimates, setIsLoadingEstimates] = useState(false);

   // State for proposals
   const [proposals, setProposals] = useState([]);
   const [isLoadingProposals, setIsLoadingProposals] = useState(false);

   // NEW: live character-length errors for the CKEditor fields that show a char count
   const [fieldLengthErrors, setFieldLengthErrors] = useState({});

   // NEW: checks length against CKEDITOR_MAX_LENGTH and stores/clears the error message
   const checkLengthError = (fieldName, value) => {
      const length = value?.length || 0;
      const maxLength = FIELD_MAX_LENGTHS[fieldName];
      setFieldLengthErrors((prev) => ({
         ...prev,
         [fieldName]:
            maxLength && length > maxLength
               ? `This field is too long. Please keep it within ${maxLength} characters.`
               : '',
      }));
   };

   // Track previous contract type ID to detect ACTUAL changes (not just navigation)
   const previousTypeIdRef = useRef(null);

   // Contract Type - RTK Query (single source of truth)
   const { data: activeContractTypes, isLoading: isLoadingContractTypes } =
      useGetActiveContractTypesQuery();
   const contractTypeList = activeContractTypes?.response || [];

   const selectedContractTypeIdValue = values.contractTypeId || '';
   const { data: contractTypeDetail } = useGetContractTypeByIdQuery(selectedContractTypeIdValue, {
      skip: !selectedContractTypeIdValue,
   });

   // When contract type detail loads, dispatch pre-fill values
   // CRITICAL: Only apply pre-fill when user explicitly selects a contract type
   // NOT on component remount (which would overwrite user edits)
   useEffect(() => {
      if (!contractTypeDetail) {
         return;
      }

      // CRITICAL: Only apply if flag indicates user just selected this type
      // (not if component is remounting with same type already selected)
      if (!previousTypeIdRef.current?.shouldApplyPreFill) {
         return;
      }

      // CRITICAL FIX: Clear old pre-fill values before setting new ones
      // This ensures that switching contract types fully resets fields
      const fieldsToReset = [
         'confidential_information',
         'amendments',
         'general_terms_and_conditions',
         'project_termination_terms',
         'payment_terms',
         'application_ownership',
         'liability',
         'legal_jurisdiction',
      ];
      fieldsToReset.forEach((key) => {
         setFieldValue(key, '');
      });

      const fieldValues =
         contractTypeDetail?.response?.field_values || contractTypeDetail?.data?.field_values || [];
      const preFill = extractPreFillValues(fieldValues);
      dispatch(setContractTypePreFillData(preFill));

      // Now set new values
      Object.entries(preFill).forEach(([key, val]) => {
         if (val) {
            setFieldValue(key, val);
         }
      });

      // Clear the flag after applying
      previousTypeIdRef.current = { shouldApplyPreFill: false };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [contractTypeDetail]);

   // Handle contract type change
   const handleContractTypeChange = (e) => {
      const typeId = e.target.value;

      // Set flag to indicate user just selected a type, so effect should apply pre-fill
      previousTypeIdRef.current = { shouldApplyPreFill: true };

      // CRITICAL FIX #1: Immediately clear ALL pre-fill fields
      const fieldsToReset = [
         'confidential_information',
         'amendments',
         'general_terms_and_conditions',
         'project_termination_terms',
         'payment_terms',
         'application_ownership',
         'liability',
         'legal_jurisdiction',
      ];
      fieldsToReset.forEach((key) => {
         setFieldValue(key, '');
      });

      // CRITICAL FIX #2: Clear pre-fill Redux state BEFORE changing typeId
      dispatch(setContractTypePreFillData(null));

      setFieldValue('contractTypeId', typeId);
      dispatch(setSelectedContractTypeId(typeId ? Number(typeId) : null));
   };

   // Use RTK Query with fixed parameters - add skip logic to prevent unnecessary fetching
   const { data: opportunityData, isLoading: isLoadingForOpportunity } = useGetopportunitiesQuery(
      { limit: 10, name: search, page },
      { skip: search === '' && page === 1 && visibleOptions.length > 0 },
   );

   // Fetch milestones using Axios
   useEffect(() => {
      const fetchMilestones = async () => {
         setIsLoadingMilestones(true);
         try {
            const token = getAuthToken();
            const response = await axios.get(`${API_URL}milestone/list`, {
               headers: {
                  Authorization: `Bearer ${token}`,
               },
            });

            if (response.data && response.data.response && response.data.response.data) {
               setMilestones(response.data.response.data);
            } else {
               setMilestones([]);
            }
         } catch (error) {
            setMilestones([]);
         } finally {
            setIsLoadingMilestones(false);
         }
      };

      fetchMilestones();
   }, []);

   // Fetch estimates using Axios
   useEffect(() => {
      const fetchEstimates = async () => {
         setIsLoadingEstimates(true);
         try {
            const token = getAuthToken();
            const response = await axios.get(`${API_URL}estimate/list`, {
               headers: {
                  Authorization: `Bearer ${token}`,
               },
            });

            if (response.data && response.data.response && response.data.response.data) {
               setEstimates(response.data.response.data);
            } else {
               setEstimates([]);
            }
         } catch (error) {
            setEstimates([]);
         } finally {
            setIsLoadingEstimates(false);
         }
      };

      fetchEstimates();
   }, []);

   // Fetch proposals using Axios
   useEffect(() => {
      const fetchProposals = async () => {
         setIsLoadingProposals(true);
         try {
            const token = getAuthToken();
            const response = await axios.get(
               `${API_URL}proposal/list?limit=10&page=0&orderBy=DESC&search=
`,
               {
                  headers: {
                     Authorization: `Bearer ${token}`,
                  },
               },
            );

            if (response.data && response.data.response && response.data.response.data) {
               setProposals(response.data.response.data);
            } else {
               setProposals([]);
            }
         } catch (error) {
            setProposals([]);
         } finally {
            setIsLoadingProposals(false);
         }
      };

      fetchProposals();
   }, []);

   // Load opportunity data only when the data changes
   useEffect(() => {
      if (!opportunityData?.response?.data) return;

      const newData = opportunityData.response.data;
      if (newData && newData.length > 0) {
         setVisibleOptions((prevOptions) => {
            // Create a map of existing IDs for faster lookup
            const existingIds = new Set(prevOptions.map((opt) => opt.id));
            // Filter out duplicates
            const uniqueNewData = newData.filter((opt) => !existingIds.has(opt.id));
            // Only update state if there are new items to add
            if (uniqueNewData.length === 0) {
               return prevOptions;
            }
            return [...prevOptions, ...uniqueNewData];
         });
      }
   }, [opportunityData?.response?.data]);

   // Initialize selected option if opportunity_id is already set
   useEffect(() => {
      // Always sync selectedOption with Formik value
      if (values.opportunity_id) {
         // Try to find the selected opportunity in visibleOptions
         const existingOpportunity = visibleOptions.find((opt) => opt.id === values.opportunity_id);
         if (existingOpportunity) {
            setSelectedOption(existingOpportunity);
         } else if (selectedOption === null || selectedOption?.id !== values.opportunity_id) {
            setSelectedOption({
               id: values.opportunity_id,
               name: values.opportunity_name || '',
            });
         }
      } else {
         setSelectedOption(null);
      }
   }, [values.opportunity_id, visibleOptions, values.opportunity_name, activeStep]);

   // Handle scrolling for infinite loading - prevent excessive fetching
   const handleScroll = (event) => {
      if (isLoadingForOpportunity) return; // Don't fetch if already loading

      const listboxNode = event.currentTarget;
      const bottom =
         listboxNode.scrollHeight - listboxNode.scrollTop <= listboxNode.clientHeight + 5;

      if (bottom) {
         setPage((prev) => prev + 1);
      }
   };

   // Handle lead selection - simplified
   const handleSelectOpportunity = (event, value, reason) => {
      // Only update if value has changed to prevent loops
      if (
         (reason === 'selectOption' && value?.id !== selectedOption?.id) ||
         (reason === 'clear' && selectedOption !== null)
      ) {
         setSelectedOption(value);
         setFieldValue('opportunity', value?.id || null);
         setFieldValue('opportunity_id', value?.id || null);
         setFieldValue('opportunity_name', value?.name || '');
      }
   };

   // Optimize search to prevent unnecessary API calls
   const handleSearch = (event, value) => {
      if (value === search) {
         return;
      }
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => {
         setSearch(value);
         setPage(1);
         setVisibleOptions([]);
      }, 500);
   };

   // Handle Add Quotation
   const handleAddQuotation = (quotationId) => {
      const selectedQuotation = contractdata?.allQuotations?.find(
         (quotation) => quotation?.id === quotationId,
      );

      if (selectedQuotation) {
         setFieldValue('quotation_id', quotationId);

         // Auto-populate service provider from selected quotation
         if (selectedQuotation?.serviceProvider?.id) {
            setFieldValue('serviceProviderId', selectedQuotation.serviceProvider.id);
         }

         // Update currency
         dispatch(setContractCurrency(selectedQuotation?.currency));
      } else {
         console.log('handleAddQuotation', 'quotation NOT found in allQuotations', { quotationId });
      }
   };

   // Defensive: Ensure contractdata.quotation and contractdata.allQuotations are always arrays
   const quotations = Array.isArray(contractdata.quotation) ? contractdata.quotation : [];
   const allQuotations = Array.isArray(contractdata.allQuotations)
      ? contractdata.allQuotations
      : [];

   // Service Provider dropdown logic
   const {
      data: providerData,
      isLoading: isLoadingForProvider,
      isError: isErrorForProvider,
      error: errorForProvider,
   } = useGetProvidersQuery();

   return (
      <div className="card stepCard">
         <Box className="card-body" sx={{ padding: '15px' }}>
            <Grid container spacing={2}>
               <Grid item md={12} xs={12}>
                  <Box className="commonBox">
                     <Grid container spacing={4}>
                        {/* Contract Type */}
                        <Grid item md={12} xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Contract Type{' '}
                              <span style={{ color: '#d32f2f', paddingLeft: '5px' }}>*</span>
                              <Chip
                                 label="Required"
                                 color="error"
                                 size="small"
                                 sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                              />
                           </Typography>
                           <FormControl fullWidth
                              error={Boolean(touched.contractTypeId && errors.contractTypeId)}
                           >
                              <InputLabel id="contract-type-label">Select Contract Type</InputLabel>
                              <Select
                                 labelId="contract-type-label"
                                 label="Select Contract Type"
                                 name="contractTypeId"
                                 onBlur={handleBlur}
                                 value={values.contractTypeId || ''}
                                 onChange={handleContractTypeChange}
                                 disabled={isLoadingContractTypes}
                              >
                                 <MenuItem value="">
                                    <em>Select a contract type</em>
                                 </MenuItem>
                                 {contractTypeList.map((ct) => (
                                    <MenuItem key={ct.id} value={ct.id}>
                                       {ct.name}
                                    </MenuItem>
                                 ))}
                              </Select>
                              {touched.contractTypeId && errors.contractTypeId && (
                                 <FormHelperText>{errors.contractTypeId}</FormHelperText>
                              )}
                           </FormControl>
                        </Grid>
                        {/* Opportunity Details */}
                        <Grid item md={6} xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Opportunity Details{' '}
                              <span
                                 style={{
                                    color: '#d32f2f',
                                    paddingLeft: '5px',
                                 }}
                              >
                                 *
                              </span>
                              <Chip
                                 label="Required"
                                 color="error"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                 }}
                              />
                           </Typography>

                           <FormControl
                              fullWidth
                              error={Boolean(touched.opportunity_id && errors.opportunity_id)}
                           >
                              <InfiniteAutocomplete
                                 required
                                 onBlur={handleBlur}
                                 value={selectedOption}
                                 onChange={(event, value, reason) =>
                                    handleSelectOpportunity(event, value, reason)
                                 }
                                 options={visibleOptions}
                                 onScroll={handleScroll}
                                 inputValue={search}
                                 setInputValue={handleSearch}
                                 total_options={'Opportunity'}
                                 loading={isLoadingForOpportunity}
                                 getOptionLabel={(option) => option.name || ''}
                                 isOptionEqualToValue={(option, value) => option.id === value.id}
                                 renderOption={(props, option) => (
                                    <li {...props} key={option.id} style={{ padding: '8px 16px' }}>
                                       <div
                                          style={{
                                             display: 'flex',
                                             flexDirection: 'column',
                                             width: '100%',
                                             overflow: 'hidden',
                                          }}
                                       >
                                          <Typography
                                             variant="body1"
                                             fontWeight="medium"
                                             sx={{
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                             }}
                                          >
                                             {option.name}
                                          </Typography>
                                          <Typography
                                             variant="caption"
                                             color="text.secondary"
                                             sx={{
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                             }}
                                          >
                                             {option.company?.name || 'No company'} •{' '}
                                             {option.status || 'No status'}
                                          </Typography>
                                       </div>
                                    </li>
                                 )}
                                 sx={{
                                    '& .MuiAutocomplete-inputRoot': {
                                       paddingTop: '8px',
                                       paddingBottom: '8px',
                                    },
                                    '& .MuiInputLabel-root': {
                                       transform: selectedOption
                                          ? 'translate(14px, -9px) scale(0.75)'
                                          : 'translate(14px, 16px) scale(1)',
                                    },
                                    '& .MuiAutocomplete-option': {
                                       paddingTop: '6px',
                                       paddingBottom: '6px',
                                    },
                                 }}
                              />
                              {touched.opportunity_id && errors.opportunity_id && (
                                 <FormHelperText>{errors.opportunity_id}</FormHelperText>
                              )}
                           </FormControl>
                        </Grid>
                        {/* Select Quotation */}
                        <Grid item md={6} xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Select Quotation
                              <Chip
                                 label="Optional"
                                 color="primary"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                    background: '#3F30FF',
                                    color: '#fff',
                                 }}
                              />
                           </Typography>
                           <FormControl fullWidth>
                              <Select
                                 labelId="quotation-select-label"
                                 label="Select Quotation"
                                 onChange={(e) => handleAddQuotation(e.target.value)}
                                 value={values.quotation_id || ''}
                                 displayEmpty
                              >
                                 <MenuItem value="" disabled>
                                    Select Quotation
                                 </MenuItem>
                                 {quotations?.map((option, index) => (
                                    <MenuItem key={option.id} value={option.id}>
                                       {allQuotations[index]?.title ||
                                          option.title ||
                                          `Quotation ${index + 1}`}
                                    </MenuItem>
                                 ))}
                              </Select>
                           </FormControl>
                        </Grid>
                        {/* Milestone */}
                        <Grid item md={6} xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Milestone
                              <Chip
                                 label="Optional"
                                 color="primary"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                    background: '#3F30FF',
                                    color: '#fff',
                                 }}
                              />
                           </Typography>
                           <FormControl fullWidth>
                              <Select
                                 labelId="milestone-select-label"
                                 label="Select Milestone"
                                 name="milestones"
                                 value={values.milestones || ''}
                                 onChange={(e) => {
                                    setFieldValue('milestones', e.target.value);
                                 }}
                                 displayEmpty
                              >
                                 <MenuItem value="" disabled>
                                    {isLoadingMilestones
                                       ? 'Loading milestones...'
                                       : 'Select Milestone'}
                                 </MenuItem>
                                 {milestones?.map((milestone) => (
                                    <MenuItem key={milestone.id} value={milestone.id}>
                                       {milestone.name}
                                    </MenuItem>
                                 ))}
                              </Select>
                           </FormControl>
                        </Grid>
                        {/* Estimate */}
                        <Grid item md={6} xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Estimate
                              <Chip
                                 label="Optional"
                                 color="primary"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                    background: '#3F30FF',
                                    color: '#fff',
                                 }}
                              />
                           </Typography>
                           <FormControl fullWidth>
                              {/* <InputLabel id="estimate-select-label">Select Estimate</InputLabel> */}
                              <Select
                                 labelId="estimate-select-label"
                                 label="Select Estimate"
                                 name="estimate"
                                 value={values.estimate || ''}
                                 onChange={(e) => {
                                    setFieldValue('estimate', e.target.value);
                                 }}
                                 displayEmpty
                              >
                                 <MenuItem value="" disabled>
                                    {isLoadingEstimates
                                       ? 'Loading estimates...'
                                       : 'Select Estimate'}
                                 </MenuItem>
                                 {estimates?.map((estimate) => {
                                    return (
                                       <MenuItem key={estimate.id} value={estimate.id}>
                                          {estimate.name}
                                       </MenuItem>
                                    );
                                 })}
                              </Select>
                           </FormControl>
                        </Grid>
                        {/* Proposal */}
                        <Grid item md={6} xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Proposal
                              <Chip
                                 label="Optional"
                                 color="primary"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                    background: '#3F30FF',
                                    color: '#fff',
                                 }}
                              />
                           </Typography>
                           <FormControl fullWidth>
                              <Select
                                 labelId="proposal-select-label"
                                 label="Select Proposal"
                                 name="proposal"
                                 value={values.proposal || ''}
                                 onChange={(e) => {
                                    setFieldValue('proposal', e.target.value);
                                 }}
                                 displayEmpty
                              >
                                 <MenuItem value="" disabled>
                                    {isLoadingProposals
                                       ? 'Loading proposals...'
                                       : 'Select Proposal'}
                                 </MenuItem>
                                 {proposals?.map((proposal) => {
                                    console.log('Proposal:', proposal);
                                    return (
                                       <MenuItem key={proposal.id} value={proposal.id}>
                                          {proposal.title}
                                       </MenuItem>
                                    );
                                 })}
                              </Select>
                           </FormControl>
                        </Grid>
                        {/* Service Provider */}
                        <Grid item md={6} xs={12}>
                           <Typography variant="h6" gutterBottom align="left">
                              Service Provider <span style={{ color: '#d32f2f' }}>*</span>
                           </Typography>
                           <FormControl
                              fullWidth
                              error={Boolean(touched.serviceProviderId && errors.serviceProviderId)}
                           >
                              <InputLabel id="service-provider-label">Service Provider</InputLabel>
                              <Select
                                 labelId="service-provider-label"
                                 label="Service Provider"
                                 name="serviceProviderId"
                                 value={values.serviceProviderId || ''}
                                 onChange={(e) => {
                                    setFieldValue('serviceProviderId', Number(e.target.value));
                                 }}
                                 onBlur={handleBlur}
                                 disabled={isLoadingForProvider}
                                 required
                              >
                                 <MenuItem value="" disabled>
                                    {isLoadingForProvider
                                       ? 'Loading...'
                                       : 'Select Service Provider'}
                                 </MenuItem>
                                 {providerData?.response?.map((provider) => (
                                    <MenuItem key={provider.id} value={provider.id}>
                                       {provider.name}
                                    </MenuItem>
                                 ))}
                              </Select>
                              {touched.serviceProviderId && errors.serviceProviderId && (
                                 <FormHelperText>{errors.serviceProviderId}</FormHelperText>
                              )}
                           </FormControl>
                        </Grid>
                        {/* Confidential Information */}
                        <Grid item xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Confidential Information{' '}
                              <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span>
                              <Chip
                                 label="Required"
                                 color="error"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                 }}
                              />
                           </Typography>
                           <Grid container spacing={2}>
                              <Grid item xs={12}>
                                 <Field name="confidential_information">
                                    {({ field }) => (<>
                                       <CKEditor
                                          editor={ClassicEditor}
                                          data={field.value}
                                          onChange={(event, editor) => {
                                             const val = editor.getData();
                                             setFieldValue('confidential_information', val);
                                             checkLengthError('confidential_information', val); {/* NEW */ }
                                          }}
                                          config={{
                                             placeholder:
                                                'Enter Confidential Information here...',
                                          }}
                                          onError={touched.confidential_information &&
                                             errors.confidential_information}
                                          helperText={
                                             touched.confidential_information &&
                                             errors.confidential_information
                                          }
                                       />
                                       {field.value && (
                                          <Typography
                                             variant="caption"
                                             color="GrayText"
                                             sx={{ mt: 0.5, display: 'block' }}
                                          >
                                             {field.value?.length} characters
                                          </Typography>
                                       )}
                                       {touched.confidential_information && errors.confidential_information && (
                                          <Typography
                                             variant="caption"
                                             sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                          >
                                             {errors.confidential_information}
                                          </Typography>
                                       )}
                                       {/* NEW: red length-limit error, shown like a helper text below the field */}
                                       {fieldLengthErrors.confidential_information && (
                                          <Typography
                                             variant="caption"
                                             sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                          >
                                             {fieldLengthErrors.confidential_information}
                                          </Typography>
                                       )}
                                    </>
                                    )}
                                 </Field>
                                 {/* <ErrorMessage
                                    name="confidential_information"
                                    component="div"
                                    style={{ color: 'red' }}
                                 /> */}
                              </Grid>
                           </Grid>
                        </Grid>

                        {/* Purpose of the Agreement */}
                        <Grid item xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Purpose of the Agreement{' '}
                              <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span>
                              <Chip
                                 label="Required"
                                 color="error"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                 }}
                              />
                           </Typography>
                           <Grid container spacing={2}>
                              <Grid item xs={12}>
                                 <Field name="purpose_of_the_agreement">
                                    {({ field }) => (<>
                                       <CKEditor
                                          editor={ClassicEditor}
                                          data={field.value}
                                          config={{
                                             placeholder:
                                                'Enter Purpose of Agreement here...',
                                          }}
                                          onChange={(event, editor) => {
                                             const val = editor.getData();
                                             setFieldValue('purpose_of_the_agreement', val);
                                             checkLengthError('purpose_of_the_agreement', val); {/* NEW */ }
                                          }}
                                          onError={touched.purpose_of_the_agreement &&
                                             errors.purpose_of_the_agreement}
                                          helperText={
                                             touched.purpose_of_the_agreement &&
                                             errors.purpose_of_the_agreement
                                          }


                                       />
                                       {field.value && (
                                          <Typography
                                             variant="caption"
                                             color="GrayText"
                                             sx={{ mt: 0.5, display: 'block' }}
                                          >
                                             {field.value?.length} characters
                                          </Typography>
                                       )}
                                       {touched.purpose_of_the_agreement && errors.purpose_of_the_agreement && (
                                          <Typography
                                             variant="caption"
                                             sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                          >
                                             {errors.purpose_of_the_agreement}
                                          </Typography>
                                       )}
                                       {/* NEW: red length-limit error, shown like a helper text below the field */}
                                       {fieldLengthErrors.purpose_of_the_agreement && (
                                          <Typography
                                             variant="caption"
                                             sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                          >
                                             {fieldLengthErrors.purpose_of_the_agreement}
                                          </Typography>
                                       )}
                                    </>
                                    )}
                                 </Field>
                                 {/* <ErrorMessage
                                    name="purpose_of_the_agreement"
                                    component="div"
                                    style={{ color: 'red' }}
                                 /> */}
                              </Grid>
                           </Grid>
                        </Grid>

                        {/* Scope of work */}
                        <Grid item xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Scope of work{' '}
                              <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span>
                              <Chip
                                 label="Required"
                                 color="error"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                 }}
                              />
                           </Typography>
                           <Grid container spacing={2}>
                              <Grid item xs={12}>
                                 <Field name="scope_of_work">
                                    {({ field }) => (<>
                                       <CKEditor
                                          editor={ClassicEditor}
                                          data={field.value}
                                          config={{ placeholder: 'Enter scope of work here...' }}
                                          onChange={(event, editor) => {
                                             const val = editor.getData();
                                             setFieldValue('scope_of_work', val);
                                             checkLengthError('scope_of_work', val); {/* NEW */ }
                                          }}
                                          onError={touched.scope_of_work &&
                                             errors.scope_of_work}
                                          helperText={
                                             touched.scope_of_work &&
                                             errors.scope_of_work
                                          }


                                       />
                                       {field.value && (
                                          <Typography
                                             variant="caption"
                                             color="GrayText"
                                             sx={{ mt: 0.5, display: 'block' }}
                                          >
                                             {field.value?.length} characters
                                          </Typography>
                                       )}
                                       {/* NEW: red length-limit error, shown like a helper text below the field */}
                                       {fieldLengthErrors.scope_of_work && (
                                          <Typography
                                             variant="caption"
                                             sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                          >
                                             {fieldLengthErrors.scope_of_work}
                                          </Typography>
                                       )}
                                       {touched.scope_of_work && errors.scope_of_work && (
                                          <Typography
                                             variant="caption"
                                             sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                          >
                                             {errors.scope_of_work}
                                          </Typography>
                                       )}
                                    </>
                                    )}
                                 </Field>
                                 {/* <ErrorMessage
                                    name="scope_of_work"
                                    component="div"
                                    style={{ color: 'red' }}
                                 /> */}
                              </Grid>
                           </Grid>
                        </Grid>

                        {/* Amendments */}
                        <Grid item xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Amendments <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span>
                              <Chip
                                 label="Required"
                                 color="error"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                 }}
                              />
                           </Typography>
                           <Grid container spacing={2}>
                              <Grid item xs={12}>
                                 <Field name="amendments">
                                    {({ field }) => (<>
                                       <CKEditor
                                          editor={ClassicEditor}
                                          data={field.value}
                                          onChange={(event, editor) => {
                                             const val = editor.getData();
                                             setFieldValue('amendments', val);
                                             checkLengthError('amendments', val); {/* NEW */ }
                                          }}
                                          config={{
                                             placeholder:
                                                'Enter Purpose of Amendments here...',
                                          }}
                                          onError={touched.amendments &&
                                             errors.amendments}
                                          helperText={
                                             touched.amendments &&
                                             errors.amendments
                                          }

                                       />
                                       {field.value && (
                                          <Typography
                                             variant="caption"
                                             color="GrayText"
                                             sx={{ mt: 0.5, display: 'block' }}
                                          >
                                             {field.value?.length} characters
                                          </Typography>
                                       )}
                                       {/* NEW: red length-limit error, shown like a helper text below the field */}
                                       {fieldLengthErrors.amendments && (
                                          <Typography
                                             variant="caption"
                                             sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                          >
                                             {fieldLengthErrors.amendments}
                                          </Typography>
                                       )}
                                       {touched.amendments && errors.amendments && (
                                          <Typography
                                             variant="caption"
                                             sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                          >
                                             {errors.amendments}
                                          </Typography>
                                       )}
                                    </>
                                    )}
                                 </Field>
                                 {/* <ErrorMessage
                                    name="amendments"
                                    component="div"
                                    style={{ color: 'red' }}
                                 /> */}
                              </Grid>
                           </Grid>
                        </Grid>

                        {/* Add Project Name Client Name, Designation, Company Name, Client Address fields */}
                        <Grid item xs={12} md={3}>
                           <TextField
                              fullWidth
                              label={
                                 <span>
                                    Project Name <span style={{ color: '#d32f2f' }}>*</span>
                                 </span>
                              }
                              name="projectName"
                              value={values.projectName}
                              onChange={(e) => setFieldValue('projectName', e.target.value)}
                              onBlur={handleBlur}
                              error={Boolean(touched.projectName && errors.projectName)}
                              helperText={touched.projectName && errors.projectName}
                           />
                        </Grid>
                        <Grid item xs={12} md={3}>
                           <TextField
                              fullWidth
                              label={<span>Client Name</span>}
                              name="client_name"
                              value={values.client_name}
                              onChange={(e) => setFieldValue('client_name', e.target.value)}
                              onBlur={handleBlur}
                              error={Boolean(touched.client_name && errors.client_name)}
                              helperText={touched.client_name && errors.client_name}
                           />
                        </Grid>
                        <Grid item xs={12} md={3}>
                           <TextField
                              fullWidth
                              label={<span>Designation</span>}
                              name="designation"
                              value={values.designation}
                              onChange={(e) => setFieldValue('designation', e.target.value)}
                              onBlur={handleBlur}
                              error={Boolean(touched.designation && errors.designation)}
                              helperText={touched.designation && errors.designation}
                           />
                        </Grid>
                        <Grid item xs={12} md={3}>
                           <TextField
                              fullWidth
                              label={<span>Company Name</span>}
                              name="companyName"
                              value={values.companyName}
                              onChange={(e) => setFieldValue('companyName', e.target.value)}
                              onBlur={handleBlur}
                              error={Boolean(touched.companyName && errors.companyName)}
                              helperText={touched.companyName && errors.companyName}
                           />
                        </Grid>
                        <Grid item xs={12} md={3}>
                           <TextField
                              fullWidth
                              label={<span>Client Address</span>}
                              name="address"
                              value={values.address}
                              onChange={(e) => setFieldValue('address', e.target.value)}
                              onBlur={handleBlur}
                              error={Boolean(touched.address && errors.address)}
                              helperText={touched.address && errors.address}
                           />
                        </Grid>
                     </Grid>
                  </Box>
               </Grid>
            </Grid>
         </Box>
      </div>
   );
}

export default StepOne;